// The ONE place that decides which report column a pointsDescriptions row
// belongs in. Three independent copies of this rule had drifted apart
// (Assessment.jsx views, MeetingDetail.jsx, MurphyReport/mapToReport.js) -
// two of them were missing the no-opportunity suppression shipped in
// release-1.10.0, so rows hidden on the report page still surfaced in the PDF
// and the live-meeting detail view. NewAssessmentDetails consumes the model
// views but shares COVERED_RATIO for its per-question colour.

// Legacy ratio threshold: a row without a grader verdict is a strength at or
// above this share of its max points.
export const COVERED_RATIO = 0.65;

// Categories the rep had no opportunity to score on (e.g. "Questions and
// Answers" on Level 1 sessions where Murphy never asks questions) belong in
// NEITHER column. New records carry notApplicable from the grader; older ones
// are caught by the mandated neutral wording (grading hard rule 3 requires
// that exact sentence, alone, when no questions were asked) - hidden
// REGARDLESS of score: the interactions category on Level 2 sessions can
// award points alongside the sentence, which rendered "Murphy did not ask any
// questions" as a green positive. Points still count; only the display goes.
const NO_OPPORTUNITY_TEXT = /(no questions were asked during this session|murphy did not ask any questions)/i;

export const isDisplayableCategory = (point) => {
    if (!point) return false;
    if (point.notApplicable) return false;
    // Informational rows (the qa-family "Overall Delivery & Fluency" read)
    // carry a score for context but are not graded categories: the band
    // already excludes them (gradeableMaxPoints), and listing one under
    // strengths at 65/100 or improvements below misreads it as a verdict.
    if (point.informational) return false;
    // The wording catch is a LEGACY adapter only: records graded since the
    // outcome enum shipped carry notApplicable from the backend, and a row
    // the grader explicitly declared pass/partial/improve must not be hidden
    // because its coaching happens to mention that Murphy asked nothing.
    if (!point.outcome && NO_OPPORTUNITY_TEXT.test(point.description || '')) return false;
    return true;
};

export const COLUMN = {
    POSITIVE: 'positive',
    IMPROVEMENT: 'improvement',
    HIDDEN: 'hidden',
};

// The grader's own column verdict (an internal enum - NEVER rendered as
// text). pass → strengths; partial/improve → improvements; not_evaluated →
// hidden. Any other/absent value falls through to the legacy ratio rules, so
// every historical record renders exactly as it did before.
const OUTCOME_COLUMN = {
    pass: COLUMN.POSITIVE,
    partial: COLUMN.IMPROVEMENT,
    improve: COLUMN.IMPROVEMENT,
    not_evaluated: COLUMN.HIDDEN,
};

/**
 * Column for a single pointsDescriptions row.
 *
 * Order matters:
 * 1. Non-displayable (notApplicable / no-opportunity wording) → HIDDEN.
 * 2. Grader-declared outcome, when present and recognized.
 * 3. Penalty rows (isPenalty flag, or any legacy negative-points row) →
 *    IMPROVEMENT. Before this rule, a −100 "Murphy Interaction Penalty"
 *    satisfied neither ratio branch and rendered in NO column - the harshest
 *    feedback in the system was invisible.
 * 4. With a real denominator: ratio ≥ 0.65 → POSITIVE, else IMPROVEMENT.
 * 5. Legacy rows without maxPoints: > 0 → POSITIVE, 0 → IMPROVEMENT.
 */
export const columnFor = (point) => {
    if (!isDisplayableCategory(point)) return COLUMN.HIDDEN;
    if (OUTCOME_COLUMN[point.outcome]) return OUTCOME_COLUMN[point.outcome];
    if (point.isPenalty || point.pointsAwarded < 0) return COLUMN.IMPROVEMENT;
    if (point.maxPoints) {
        return point.pointsAwarded / point.maxPoints >= COVERED_RATIO
            ? COLUMN.POSITIVE
            : COLUMN.IMPROVEMENT;
    }
    return point.pointsAwarded > 0 ? COLUMN.POSITIVE : COLUMN.IMPROVEMENT;
};

export const positiveRows = (pointsDescriptions) =>
    (pointsDescriptions || []).filter((p) => columnFor(p) === COLUMN.POSITIVE);

export const improvementRows = (pointsDescriptions) =>
    (pointsDescriptions || []).filter((p) => columnFor(p) === COLUMN.IMPROVEMENT);

// Informational rows (the qa-family "Overall Delivery & Fluency" read) are in
// NEITHER column and never in the band, but their coaching is worth reading:
// surfaces render them as a neutral note, not a verdict (Toby, 2026-09-04).
export const informationalRows = (pointsDescriptions) =>
    (pointsDescriptions || []).filter((p) => p && p.informational && !p.isPenalty && p.description);
